import { dollarsToCents } from "./review-domain.ts";

export type ProjectPart = { id: string; description: string; quantity: string; unitSell: string; unitCost?: string; markupPercent?: string; sourceType?: "unassigned" | "in-house" | "supplier"; sourceConfirmed?: boolean; supplierId?: string; catalogItemId?: string };
export type ProjectFinding = {
  component?: string;
  decision: string;
  evidenceReviewed: boolean;
  derivedFromFindingId?: string;
  manufacturer: string;
  reportNumber: string;
  assetId: string;
  modelNumber: string;
  serialNumber: string;
  parts: ProjectPart[];
};
export type ProjectManufacturerRequest = {
  routingKey: string;
  primarySupplierId: string;
  manufacturer: string;
  projectNumber: string;
  status: string;
  assets: Array<{ reportNumber: string; assetId: string; component?: string; model: string; serial: string; parts: ProjectPart[] }>;
};

export type ProjectSupplierQuotation = {
  manufacturer: string;
  supplierId: string;
  partPrices: Record<string, string>;
  partMarkups?: Record<string, string>;
  defaultMarkupPercent?: string;
};

export type RfqIdentityIssue = { assetId: string; reportNumber: string; component: string; partIds: string[]; parts: string[]; fields: string[] };

/** One validation rule for the RFQ preview and server-side send gate. */
export function rfqIdentityIssues(request: ProjectManufacturerRequest): RfqIdentityIssue[] {
  const unresolved = (value: string) => !value?.trim() || /confirmation required|^(?:unknown|confirm|n\/?a|not stated|-)$/i.test(value.trim());
  return request.assets.flatMap((asset) => {
    const fields: string[] = [];
    if (unresolved(request.manufacturer)) fields.push("manufacturer");
    if (unresolved(asset.model)) fields.push("model");
    if (unresolved(asset.serial)) fields.push("serial number");
    if (unresolved(asset.assetId)) fields.push("asset identifier");
    if (unresolved(asset.reportNumber)) fields.push("report number");
    if (asset.component === "Component review") fields.push("repair component (hoist or trolley)");
    return fields.length ? [{ assetId: asset.assetId, reportNumber: asset.reportNumber, component: asset.component || "Component not specified", partIds: asset.parts.map((part) => part.id), parts: asset.parts.map((part) => part.description), fields }] : [];
  });
}

export function validMarkupPercent(value: string) {
  return /^\d+(?:\.\d{1,2})?$/.test(value.trim()) && Number(value) <= 1000;
}

export function supplierSellPrice(cost: string, markupPercent: string) {
  const costCents = dollarsToCents(cost);
  if (costCents === null) throw new Error("Enter a valid returned supplier cost.");
  if (!validMarkupPercent(markupPercent)) throw new Error("Enter a markup between 0% and 1000% with at most two decimal places.");
  const [whole = "0", fraction = ""] = markupPercent.trim().split(".");
  const basisPoints = Number.parseInt(whole, 10) * 100 + Number.parseInt(fraction.padEnd(2, "0"), 10);
  const sellCents = Math.floor((costCents * (10_000 + basisPoints) + 5_000) / 10_000);
  if (!Number.isSafeInteger(sellCents)) throw new Error("The marked-up supplier price is too large.");
  return { costCents, sellCents, sellPrice: (sellCents / 100).toFixed(2), markupPercent: Number(markupPercent).toFixed(2) };
}

export function validSupplierPrice(value: string) {
  return /^\$?\d+(?:\.\d{1,2})?$/.test(value.trim().replaceAll(",", ""));
}

export function applySupplierQuotationPrice(
  findings: ProjectFinding[],
  quotation: ProjectSupplierQuotation,
  partId: string,
): ProjectFinding[] {
  const price = quotation.partPrices[partId]?.trim() ?? "";
  if (!validSupplierPrice(price)) throw new Error("Enter a valid returned supplier cost before selecting it.");
  const markup = quotation.partMarkups?.[partId]?.trim() || quotation.defaultMarkupPercent?.trim() || "0";
  const calculated = supplierSellPrice(price, markup);
  let matched = false;
  const next = findings.map((finding) => {
    if (finding.decision !== "estimate" || !finding.evidenceReviewed) return finding;
    const parts = finding.parts.map((part) => {
      if (part.id !== partId) return part;
      const routeMatches = part.sourceType === "supplier" && finding.manufacturer === quotation.manufacturer;
      if (!routeMatches) return part;
      matched = true;
      return { ...part, unitCost: (calculated.costCents / 100).toFixed(2), markupPercent: calculated.markupPercent, unitSell: calculated.sellPrice };
    });
    return parts === finding.parts ? finding : { ...finding, parts };
  });
  if (!matched) throw new Error("That supplier price does not belong to a reviewed part for this manufacturer.");
  return next;
}

export function buildProjectManufacturerRequests(projectNumber: string, findings: ProjectFinding[]): ProjectManufacturerRequest[] {
  const normalizedProject = projectNumber.trim();
  if (!normalizedProject) return [];
  const groups = new Map<string, ProjectManufacturerRequest>();
  for (const finding of findings.filter((item) => item.decision === "estimate" && item.evidenceReviewed && item.parts.length > 0)) {
    const manufacturer = finding.manufacturer.trim() || "Manufacturer confirmation required";
    for (const part of finding.parts.filter((item) => item.sourceType === "supplier" && item.sourceConfirmed && item.supplierId?.trim())) {
      const primarySupplierId = part.supplierId!.trim();
      const routingKey = `${encodeURIComponent(manufacturer)}::${primarySupplierId}`;
      const request = groups.get(routingKey) ?? { routingKey, primarySupplierId, manufacturer, projectNumber: normalizedProject, status: "Draft", assets: [] };
      let asset = request.assets.find((item) => item.assetId === finding.assetId && item.reportNumber === finding.reportNumber && item.component === (finding.component || "") && item.model === finding.modelNumber && item.serial === finding.serialNumber);
      if (!asset) {
        asset = { reportNumber: finding.reportNumber, assetId: finding.assetId, component: finding.component || "", model: finding.modelNumber, serial: finding.serialNumber, parts: [] };
        request.assets.push(asset);
      }
      asset.parts.push({ ...part });
      groups.set(routingKey, request);
    }
  }
  return [...groups.values()];
}

export function projectSupplierPricingComplete(findings: ProjectFinding[]) {
  const included = findings.filter((finding) => finding.decision === "estimate");
  return included.length > 0 && included.every((finding) => finding.parts.every((part) => part.sourceConfirmed && validSupplierPrice(part.unitSell)));
}
